/** @jsxImportSource @emotion/react */

import {css} from "@emotion/react";
import Button from "components/Button";
import {TextInput} from "components/TextInput";
import {useAuth} from "hooks/auth";
import {useEffect, useRef, useState} from "react";
import {getHtmlTitle} from "utils";
import {ReactComponent as AeigLogo} from "assets/images/AeigLogo.svg";
import buildingsImage from "assets/images/buildings.jpg";
import SplitScreen from "components/SplitScreen";
import {Anchor, H1} from "components/elements";
import AlertBar from "components/AlertBar";
import Link from "components/Link";
import {useApiQuery} from "hooks/api";
import {getOffice365LoginUrl, ErrorMessages} from "api/auth";

export default function Login() {
  const {login, isLoggingIn} = useAuth();
  const [loginError, setLoginError] = useState<string | null>(null);
  const usernameRef = useRef<any>(null);
  const passwordRef = useRef<any>(null);
  const {data: office365LoginUrl} = useApiQuery(getOffice365LoginUrl)();
  useEffect(() => {
    window.document.title = getHtmlTitle("Login");
  });
  return (
    <SplitScreen imageurl={buildingsImage}>
      <form
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 20px;
          overflow: auto;
        `}
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await login({
              username: usernameRef.current.value,
              password: passwordRef.current.value,
            });
          } catch (error: any) {
            console.log("Login error:", error);
            console.log("Error response:", error?.response);
            console.log("Error data:", error?.response?.data);

            const errorString =
              error ||
              error?.response?.data?.error ||
              error?.error ||
              error?.message;

            // Check for specific error messages using the constants
            if (errorString === ErrorMessages.INVALID_CREDENTIALS) {
              setLoginError("Invalid Credentials");
            } else if (errorString === ErrorMessages.LOGIN_WITH_MICROSOFT_ONLY) {
              setLoginError("Please use Login With Microsoft");
            } else {
              setLoginError("Invalid Credentials");
            }
          }
        }}
      >
        <Anchor href={process.env.REACT_APP_AEIG_INSURANCE_WEBSITE_LINK}>
          <AeigLogo />
        </Anchor>
        <H1>Login</H1>
        <TextInput
          label="Email"
          ref={usernameRef}
          type="username"
          name="username"
          error={!!loginError}
          autoFocus
          onFocus={() => setLoginError(null)}
        />
        <TextInput
          label="Password"
          ref={passwordRef}
          type="password"
          name="password"
          error={!!loginError}
          onFocus={() => setLoginError(null)}
        />
        <Button type="submit" fullwidth isLoading={isLoggingIn}>
          Log in
        </Button>
        {loginError && (
          <AlertBar status="error">
            {loginError === "Please use Login With Microsoft" &&
            process.env.REACT_APP_SSO_LOGIN &&
            office365LoginUrl ? (
              <Anchor
                href={office365LoginUrl}
                css={css`
                  color: white !important;
                  text-decoration: underline;
                  &:hover {
                    color: white !important;
                    text-decoration: none;
                  }
                `}
              >
                {loginError}
              </Anchor>
            ) : (
              loginError
            )}
          </AlertBar>
        )}
        <div>
          <Link to={"/password/reset"}>Forgot Password</Link>
        </div>
        <div>
          <Link to={"/insured/register"}>Insured Register</Link>
        </div>
        <div>
          <Link to={"/payments/guest"}>Express Pay</Link>
        </div>
        <div>
          {process.env.REACT_APP_SSO_LOGIN && (
            <Anchor href={office365LoginUrl}>Login With Microsoft</Anchor>
          )}
        </div>
        {process.env.REACT_APP_CURRENT_VERSION && (
          <p
            css={(theme) => css`
              ${theme.text.p3}
            `}
          >
            Version {process.env.REACT_APP_CURRENT_VERSION}
          </p>
        )}
      </form>
    </SplitScreen>
  );
}
